import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { NavDivider } from "../../assets/icons";
import { CardContainer } from "./CardContainer";
import { SectionContainer } from "./SectionContainer";
import { Header } from "./Header";
import { useAppSelector } from "../../hooks/hooks";
import { NavDividerProps } from "./types";

const ViewAll: React.FC<{
  onSignInClick?: () => void;
  onSignUpClick?: () => void;
}> = ({ onSignInClick, onSignUpClick }) => {
  const location = useLocation();
  const { data: appData } = useAppSelector((state) => state.appData);
  const selectedCountry = useAppSelector(
    (state) => state.locale.selectedCountry,
  );

  // Extract the last path segment as the category key
  const path = location.pathname;
  const pathSegment = path.split("/").filter(Boolean).pop() || "";

  // Derive display title
  const formattedTitle =
    pathSegment?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ||
    "Plans";

  const providers = appData?.[pathSegment]?.providers ?? [];

  const productPlans = appData?.[pathSegment]?.plans ?? [];

  const providersWithDiscounts = useMemo(() => {
    return providers.map((provider: { id: any }) => {
      const providerPlans = productPlans.filter(
        (plan: {
          category: { provider_id: any; provider: { id: any } };
          countries: number[];
        }) => {
          const matchesProvider =
            plan.category?.provider_id === provider.id ||
            plan.category?.provider?.id === provider.id;

          const matchesCountry = selectedCountry
            ? plan.countries?.includes(selectedCountry.id)
            : true;

          return matchesProvider && matchesCountry;
        },
      );

      const discounts = providerPlans.map(
        (plan: { discount: string; wallet_price: string }) => {
          const numericDiscount = Number(plan.discount) || 0;
          const numericWalletPrice = Number(plan.wallet_price) || 0;
          const hasDiscount =
            numericDiscount > 0 && numericDiscount > numericWalletPrice;

          const discountPercent = hasDiscount
            ? ((numericDiscount - numericWalletPrice) / numericDiscount) * 100
            : 0;
          return discountPercent;
        },
      );

      const maxDiscount = discounts.length > 0 ? Math.max(...discounts, 0) : 0;

      return {
        ...provider,
        discount: maxDiscount,
      };
    });
  }, [providers, productPlans, selectedCountry]);

  return (
    <div>
      <div className="px-14 pt-14 pb-2">
        <div className="row-1 gap-[2px] place-items-center">
          {/* Dynamic navigation links */}
          <Navigation title="Direct Top-up Product Details" />
          <NavDivider />
          <Navigation title={formattedTitle} />
        </div>
      </div>

      <div>
        {providers && providers.length > 0 && (
          <SectionContainer>
            <Header
              icon="/png/PrepaidServices.png"
              title={formattedTitle}
              viewAllUrl={pathSegment}
            />
            <CardContainer
              cardData={providersWithDiscounts}
              url={pathSegment}
            />
          </SectionContainer>
        )}
      </div>
    </div>
  );
};

export default ViewAll;

const Navigation: React.FC<NavDividerProps> = ({ href, title }) => (
  <a
    href={href || "#"} // Default to "#" if href is not provided
    className="satoshi-xs-bold-toggleBG-dark dark:text-white"
  >
    {title}
  </a>
);
